import { ChevronRight } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import {
  getReferenceRowLabel,
  type MessageReference,
} from '@/components/chat/bubbles/references/messageReferences';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { getAccessibilityProps } from '@/util/accessibilityProps';

interface ReferenceFootnoteRowProps {
  reference: MessageReference;
  position: 'left' | 'right';
  onPress: (reference: MessageReference) => void;
}

/**
 * A numbered reference under a message: `[N]`, the article's name on one
 * line, and a chevron saying the row opens something rather than leaving the
 * app. The full citation lives in the reference sheet the row opens, so the
 * footer stays a compact index however long the citations are.
 *
 * The title is not underlined: an underline promises a link out, and the row
 * opens the sheet instead, so the chevron carries the affordance. Each row is
 * 32px tall, which keeps the list reading as a compact index; 48px rows spaced
 * it out like a menu. That clears WCAG 2.5.8's 24px minimum. A hit slop is
 * not added because it would overlap the neighbouring row.
 *
 * `flex-1` on the title, not `shrink`, is load-bearing on iOS: with `shrink`
 * the text reports its unwrapped intrinsic height before `numberOfLines`
 * applies, and the bubble grows to fit lines it never draws.
 */
export function ReferenceFootnoteRow({ reference, position, onPress }: ReferenceFootnoteRowProps) {
  const title = getReferenceRowLabel(reference);

  const tintClass = position === 'left' ? 'text-primary-600' : 'text-typography-0';

  return (
    <Pressable
      className="min-w-0"
      onPress={() => onPress(reference)}
      {...getAccessibilityProps({
        label: `Reference ${reference.number}: ${title}`,
        role: 'button',
        hint: 'Shows the full reference',
      })}>
      <HStack space="xs" className="min-h-8 min-w-0 items-center">
        <Text size="xs" className={twJoin('shrink-0 font-medium', tintClass)}>
          {`[${reference.number}]`}
        </Text>
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          className={twJoin('min-w-0 flex-1 font-medium text-sm', tintClass)}>
          {title}
        </Text>
        <Icon as={ChevronRight} size="xs" className={twJoin('shrink-0', tintClass)} />
      </HStack>
    </Pressable>
  );
}
